import { useCallback, useEffect, useMemo, useRef } from 'react';

import {
  PageViewedTracker,
  useButtonPressedCallback
} from '@zenbusiness/application-commons-clickstream';
import { WebView } from '@zenbusiness/application-commons-webview';
import Box from '@zenbusiness/zui/Box';
import Heading from '@zenbusiness/zui/Heading';
import Skeleton from '@zenbusiness/zui/Skeleton';
import Stack from '@zenbusiness/zui/Stack';
import Text from '@zenbusiness/zui/Text';
import { debounce } from 'lodash';
import PropTypes from 'prop-types';
import { useNavigate, useLocation } from 'react-router-dom';

import { DB_APPLICATION_STATUS } from '#client/constants/application';
import { MODAL_NAMES } from '#client/constants/modals';
import useApplicationStatusContext from '#client/hooks/useApplicationStatusContext';
import useBreakpoints from '#client/hooks/useBreakpoints';
import useSCorpBankingPrice from '#client/hooks/useSCorpBankingPrice';

import PageWrapper from './PageWrapper';
import {
  BankingImage,
  ButtonStyled,
  DashboardWrapper,
  DividerStyled,
  SharedAssetStyled
} from './ProductGroup.styles';

export default function ProductGroup({ callback = () => {} }) {
  const { isWebView } = WebView;
  const { pathname } = useLocation();
  const requestRef = useRef(null);
  const {
    sizes: { isDesktop }
  } = useBreakpoints();
  const { appStatus, loading } = useApplicationStatusContext();
  const navigate = useNavigate();
  const { bankingPrice } = useSCorpBankingPrice();

  const buttonRef = useRef(null);

  /**
   * Handle show or hide banner when scrolling out of view of the in-line CTA
   */
  const scrollHandler = useCallback(() => {
    try {
      // When user on webview, there is no header
      let headerHeight = 0;
      if (!isWebView) {
        headerHeight = isDesktop ? 92 : 80;
      }
      // The space between button and the top of viewport
      const spaceToTop =
        buttonRef.current.getBoundingClientRect().y - headerHeight + buttonRef.current.offsetHeight;

      if (spaceToTop > 0) {
        callback(false);
        return;
      }
      callback(true);
    } catch (error) {
      callback(false);
    }
  }, [callback, isDesktop, isWebView]);

  const handleScroll = debounce(
    useCallback(() => {
      if (requestRef.current) {
        return;
      }

      requestRef.current = requestAnimationFrame(() => {
        scrollHandler();
        requestRef.current = null;
      });
    }, [scrollHandler]),
    100
  );

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);

    // Call the handler immediately to set the initial state
    scrollHandler();

    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (requestRef.current) {
        cancelAnimationFrame(requestRef.current);
      }
    };
  }, [handleScroll, scrollHandler]);

  const trackButtonClick = useButtonPressedCallback({
    elementText:
      appStatus === DB_APPLICATION_STATUS.NOT_STARTED ? 'Apply now' : 'Finish Application',
    elementName: 'Apply_now_upper_banner',
    appPageName: 'banking_upsell_dashboard',
    dynamicProperties: {
      pageType: 'banking_upsell_dashboard'
    }
  });

  const handleClick = () => {
    /**
     * Question: How many users click the button?
     */
    trackButtonClick();

    navigate(`?modal=${MODAL_NAMES.APPLICATION_INIT_MODAL}`);
  };

  const properties = useMemo(
    () => ({
      customDestinationPageName_: 'banking_upsell_dashboard',
      appPageName: 'banking_upsell_dashboard',
      appSection: 'banking',
      experienceOrigin: 'dashboard'
    }),
    []
  );

  return (
    <PageWrapper>
      <PageViewedTracker
        {...properties}
        key={pathname}
      />
      <DashboardWrapper>
        <Stack
          gap="24px"
          paddingBottom="40px"
          position="relative"
          zIndex={1}
        >
          <Heading
            family="secondary"
            size="4xl"
            weight="bold"
          >
            ZenBusiness Banking
          </Heading>
          <Heading weight="bold">Separate your business finances and stay protected</Heading>
          <DividerStyled />
          <Box
            alignItems="center"
            display="flex"
            flexDirection={{ xs: 'column', md: 'row' }}
            gap={{ xs: '36px', md: '200px' }}
          >
            <Text
              color="primary"
              size="md"
            >
              Get a business bank account for just {bankingPrice}, and no minimum balance to keep.
              Apply in a few clicks, and start smart with your finances protected.
            </Text>
            <Box width={{ xs: '100%', md: 'auto' }}>
              {loading ? (
                <Skeleton />
              ) : (
                <ButtonStyled
                  color="secondary"
                  onClick={handleClick}
                  ref={buttonRef}
                  size={isDesktop ? 'medium' : 'large'}
                  variant="contained"
                >
                  {appStatus === DB_APPLICATION_STATUS.NOT_STARTED
                    ? 'Apply now'
                    : 'Finish Application'}
                </ButtonStyled>
              )}
            </Box>
          </Box>
        </Stack>
        <Box
          display="flex"
          justifyContent="center"
          marginTop={{ xs: '-3%', md: '-12%' }}
          minHeight={{ xs: '200px', md: '600px' }}
          position="relative"
        >
          <Box
            position="absolute"
            top={0}
            width="100%"
          >
            {isDesktop ? (
              <SharedAssetStyled
                alt=""
                src="/s2/vector/mp-lp-carousel-background-lg.svg"
              />
            ) : (
              <SharedAssetStyled
                alt=""
                src="/s2/vector/mp-lp-carousel-background-xs.svg"
              />
            )}
          </Box>
          <Box
            marginTop="20%"
            mx="auto"
            width="80%"
            zIndex={2}
          >
            <BankingImage
              alt=""
              src="/s2/vector/bank-locked-desktop-mobile-product-group.svg"
            />
          </Box>
        </Box>
        <Box />
      </DashboardWrapper>
    </PageWrapper>
  );
}

ProductGroup.propTypes = {
  callback: PropTypes.func
};
